import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import useThemeStyles from '../../../hooks/useThemeStyles';

export const FIELD_TYPES = ['string', 'number', 'boolean', 'id', 'ref', 'array', 'object', 'enum'];

/**
 * Dumb entity editor: name + field rows. All changes flow up via onChange.
 */
const EntityCard = ({ entity, onChange, onDelete }) => {
  const { colors } = useThemeStyles();

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    input: { color: colors.text.primary },
    '& .MuiSelect-select': { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  const updateField = (index, key, value) => {
    const fields = entity.fields.map((f, i) => (i === index ? { ...f, [key]: value } : f));
    onChange({ ...entity, fields });
  };

  const addField = () => {
    onChange({ ...entity, fields: [...entity.fields, { name: '', type: 'string', note: '' }] });
  };

  const removeField = (index) => {
    onChange({ ...entity, fields: entity.fields.filter((_, i) => i !== index) });
  };

  return (
    <Card
      variant="outlined"
      sx={{ backgroundColor: 'transparent', borderColor: colors.border.subtle }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label="Entity name"
            size="small"
            value={entity.name}
            onChange={(e) => onChange({ ...entity, name: e.target.value })}
            sx={{ ...textFieldSx, flex: 1 }}
          />
          <Tooltip title="Delete entity">
            <IconButton size="small" onClick={onDelete} sx={{ color: colors.icon.secondary }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {entity.fields.length === 0 && (
          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
            No fields yet.
          </Typography>
        )}

        {entity.fields.map((field, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="field name"
              size="small"
              value={field.name}
              onChange={(e) => updateField(index, 'name', e.target.value)}
              sx={{ ...textFieldSx, flex: 1.2 }}
            />
            <TextField
              select
              size="small"
              value={field.type}
              onChange={(e) => updateField(index, 'type', e.target.value)}
              sx={{ ...textFieldSx, width: 110 }}
            >
              {FIELD_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              placeholder="note"
              size="small"
              value={field.note || ''}
              onChange={(e) => updateField(index, 'note', e.target.value)}
              sx={{ ...textFieldSx, flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => removeField(index)}
              sx={{ color: colors.icon.secondary }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addField}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', color: colors.interactive.primary }}
        >
          Add Field
        </Button>
      </CardContent>
    </Card>
  );
};

EntityCard.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        note: PropTypes.string,
      })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default EntityCard;
